import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { CalendarIcon } from 'lucide-react';
import { NoConsentFilters } from '../../../api/noConsentApi';

// Import des composants UI
import { Button } from '../../ui/button';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { MultiSelect } from '../../ui/multi-select';
import { Label } from '../../ui/label';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { cn } from '../../../utils/utils';

interface NoConsentFiltersProps {
  filters: NoConsentFilters;
  filterOptions: {
    sites: string[];
  } | null;
  onChange: (filters: NoConsentFilters) => void;
  isLoading: boolean;
}

export function NoConsentFiltersComponent({ 
  filters, 
  filterOptions,
  onChange, 
  isLoading 
}: NoConsentFiltersProps) {
  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = useState<string>('last7days');

  // Fonction pour détecter quel preset correspond aux dates actuelles
  const detectPresetFromDates = (startDate?: string, endDate?: string): string => {
    if (!startDate || !endDate) return '';
    
    const now = dayjs();
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    // Vérifier si c'est aujourd'hui (pour endDate)
    const isEndToday = end.isSame(now, 'day');
    
    if (isEndToday) {
      const daysDiff = now.diff(start, 'day');
      if (daysDiff === 7) return 'last7days';
      if (daysDiff === 14) return 'last14days';
      if (daysDiff === 30) return 'last30days';
    }
    
    // Vérifier si c'est le mois dernier complet
    const lastMonth = now.subtract(1, 'month');
    const isLastMonth = start.isSame(lastMonth.startOf('month'), 'day') && 
                       end.isSame(lastMonth.endOf('month'), 'day');
    if (isLastMonth) return 'lastMonth';
    
    return ''; // Dates personnalisées
  };

  // Synchroniser selectedPreset avec les dates reçues en props
  useEffect(() => {
    const detectedPreset = detectPresetFromDates(filters.startDate, filters.endDate);
    setSelectedPreset(detectedPreset);
  }, [filters.startDate, filters.endDate]);

  const handlePresetChange = (preset: string) => {
    const now = dayjs();
    let startDate: string;
    let endDate = now.format('YYYY-MM-DD');

    switch (preset) {
      case 'last7days':
        startDate = now.subtract(7, 'day').format('YYYY-MM-DD');
        break;
      case 'last14days':
        startDate = now.subtract(14, 'day').format('YYYY-MM-DD');
        break;
      case 'last30days':
        startDate = now.subtract(30, 'day').format('YYYY-MM-DD');
        break;
      case 'lastMonth':
        const lastMonth = now.subtract(1, 'month');
        startDate = lastMonth.startOf('month').format('YYYY-MM-DD');
        endDate = lastMonth.endOf('month').format('YYYY-MM-DD');
        break;
      default:
        return;
    }

    setSelectedPreset(preset);
    onChange({
      ...filters,
      startDate,
      endDate
    });
  };

  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedPreset(''); // Désélectionner le preset
      onChange({
        ...filters,
        startDate: dayjs(date).format('YYYY-MM-DD')
      });
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedPreset(''); // Désélectionner le preset
      onChange({
        ...filters,
        endDate: dayjs(date).format('YYYY-MM-DD')
      });
    }
  };

  const handleSitesChange = (sites: string[]) => {
    onChange({
      ...filters,
      sites: sites.length > 0 ? sites : undefined
    });
  };

  const resetFilters = () => {
    setSelectedPreset('last7days');
    onChange({
      startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      sites: undefined
    });
  };

  const siteOptions = filterOptions?.sites?.map(site => ({
    value: site,
    label: site
  })) || [];

  return (
    <div className="flex gap-6">
      {/* Presets de dates avec tabs horizontales */}
      <div className="space-y-2">
        <Label>{t('noconsent.filters.dateRange')}</Label>
        <Tabs value={selectedPreset} onValueChange={handlePresetChange} className="w-full">
          <TabsList className="grid w-full grid-cols-4 h-auto">
            <TabsTrigger value="last7days" className="py-2 px-3 w-auto">
              {t('noconsent.filters.presets.last7days')}
            </TabsTrigger>
            <TabsTrigger value="last14days" className="py-2 px-3 w-auto">
              {t('noconsent.filters.presets.last14days')}
            </TabsTrigger>
            <TabsTrigger value="last30days" className="py-2 px-3 w-auto">
              {t('noconsent.filters.presets.last30days')}
            </TabsTrigger>
            <TabsTrigger value="lastMonth" className="py-2 px-3 w-auto">
              {t('noconsent.filters.presets.lastMonth')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Filtres à droite */}
      <div className="flex-1">
        <div className="grid grid-cols-4 gap-3">
          {/* Date de début */}
          <div className="space-y-1 pt-2">
            <Label className="text-sm">{t('noconsent.filters.startDate')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full h-9 justify-start text-left font-normal",
                    !filters.startDate && "text-muted-foreground"
                  )}
                  disabled={isLoading}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.startDate ? (
                    dayjs(filters.startDate).format('DD/MM/YYYY')
                  ) : (
                    <span>{t('dashboard.date.select')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.startDate ? dayjs(filters.startDate).toDate() : undefined}
                  onSelect={handleStartDateChange}
                  disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Date de fin */}
          <div className="space-y-1 pt-2">
            <Label className="text-sm">{t('noconsent.filters.endDate')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full h-9 justify-start text-left font-normal",
                    !filters.endDate && "text-muted-foreground"
                  )}
                  disabled={isLoading}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {filters.endDate ? (
                    dayjs(filters.endDate).format('DD/MM/YYYY')
                  ) : (
                    <span>{t('dashboard.date.select')}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={filters.endDate ? dayjs(filters.endDate).toDate() : undefined}
                  onSelect={handleEndDateChange}
                  disabled={(date) => {
                    return date > new Date() || 
                           date < new Date("1900-01-01") ||
                           (filters.startDate ? date < dayjs(filters.startDate).toDate() : false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Sélection des sites */}
          <div className="space-y-1 pt-2">
            <Label className="text-sm">{t('noconsent.filters.sites')}</Label>
            <MultiSelect
              options={siteOptions}
              selected={filters.sites || []}
              onChange={handleSitesChange}
              placeholder={t('noconsent.filters.selectSites')}
            />
          </div>

          {/* Bouton de réinitialisation */}
          <div className="flex items-end pt-2">
            <Button 
              variant="outline" 
              onClick={resetFilters}
              disabled={isLoading}
              className="w-full h-9"
            >
              {t('noconsent.filters.reset')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}